import { projectTypes } from '@/constants/projectTypes'
import { slugs } from '@/constants/slugs'
import { FlowPhaseForStatusCheck, isInFreeAccess, isInGuildAccess, isStreaming } from '@/services/PhaseManagerService'
import { willBeInTheaters, inTheaters } from '@/services/ProjectPhaseService'
import { ProjectMeta } from '@/services/ProjectsService'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { useLocale } from '../LocaleUtil'

const EXCLUDE_FROM_START_WATCHING = new Set([slugs.forYourConsideration, slugs.insideAngelStudios, slugs.theChosen])

// Some projects have "Episodes" that are actually teaser trailers, and we don't want those to show up in the Start Watching carousel
export const isProjectExcludedFromStartWatching = (slug: string): boolean => {
  return EXCLUDE_FROM_START_WATCHING.has(slug)
}

const IS_ANGEL_BEHIND_THE_SCENES = new Set([slugs.forYourConsideration, slugs.insideAngelStudios])

// These projects use the Season/Episode structure but they are actually behind the scenes videos, teasers and torches. We don't want to do things like show Attribution for these, and we want to build meta tags correctly as well
export const isAngelBehindTheScenes = (slug: string): boolean => {
  return IS_ANGEL_BEHIND_THE_SCENES.has(slug)
}

const getDefaultTitle = (projectType: string, name: string, t: TranslateFunction): string => {
  return projectType === projectTypes.movie
    ? t('watchMovieOnlineDefaultAngel', `{{title}} | Watch Movie Online | Angel`, { title: name })
    : t('watchShowOnlineDefaultAngel', `{{title}} | Watch Online | Angel`, { title: name })
}

const getGenericDefaultTitle = (name: string, t: TranslateFunction): string => {
  return t('genericProjectDefaultAngel', `{{title}} | Angel`, { title: name })
}

const getEarlyAccessTitle = (name: string, t: TranslateFunction): string => {
  return t('earlyAccessStreamingTitleAngel', `{{title}} | Now Streaming for Angel Guild Members | Angel`, {
    title: name,
  })
}

const getFreeAccessTitle = (projectType: string, name: string, t: TranslateFunction): string => {
  return projectType === projectTypes.movie
    ? t('watchMovieOnlineForFreeAngel', `{{title}} | Watch Movie Online For Free | Angel`, { title: name })
    : t('watchShowOnlineForFreeAngel', `{{title}} | Watch Online For Free | Angel`, { title: name })
}

const getInTheatersTitle = (name: string, t: TranslateFunction, date?: string): string => {
  return t('inTheatersDateTitleAngel', `{{title}} | Coming to Theaters {{date}} | Angel`, { title: name, date })
}

const getInTheatersSoonTitle = (name: string, t: TranslateFunction): string => {
  return t('inTheatersSoonAngel', `{{title}} | Coming to Theaters Soon | Angel`, { title: name })
}

const getInTheatersNowTitle = (name: string, t: TranslateFunction): string => {
  return t('inTheatersNowAngel', '{{title}} | In Theaters Now | Angel', { title: name })
}

function getTheatricalTitle(
  name: string,
  t: TranslateFunction,
  primaryFlowPhases: FlowPhaseForStatusCheck[] | null | undefined,
  locale: string,
): string {
  const theatricalPhase = primaryFlowPhases?.filter((phase) => phase?.phaseSlugEnum === 'theatrical')?.[0] ?? null
  if (theatricalPhase?.releaseWindows?.[0]?.start && !theatricalPhase?.releaseWindows?.[0]?.startSpecificity) {
    return getInTheatersTitle(
      name,
      t,
      new Date(theatricalPhase.releaseWindows[0].start).toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    )
  } else {
    return getInTheatersSoonTitle(name, t)
  }
}

const slugTitleMap: {
  [key: string]: { titleFunction: (name: string, t: TranslateFunction) => string }
} = {
  [slugs.insideAngelStudios]: { titleFunction: (name: string) => name },
}

interface ProjectMetaTitleProps extends ProjectMeta {
  ticketStatus?: TicketsStatusReference | null
}

export const useProjectMetaTitle = ({
  name,
  projectType,
  slug,
  primaryFlowPhases,
  ticketStatus,
}: ProjectMetaTitleProps) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()

  let title

  if (slugTitleMap[slug]) {
    title = slugTitleMap[slug]!.titleFunction(name, t)
  } else if (isInFreeAccess(primaryFlowPhases ?? [])) {
    title = getFreeAccessTitle(projectType, name, t)
  } else if (isInGuildAccess(primaryFlowPhases ?? [])) {
    title = getEarlyAccessTitle(name, t)
  } else if (inTheaters(ticketStatus)) {
    title = getInTheatersNowTitle(name, t)
  } else if (willBeInTheaters(ticketStatus)) {
    title = getTheatricalTitle(name, t, primaryFlowPhases, locale)
  } else if (!primaryFlowPhases || (primaryFlowPhases && !isStreaming(primaryFlowPhases ?? []))) {
    title = getGenericDefaultTitle(name, t)
  } else {
    title = getDefaultTitle(projectType, name, t)
  }

  return { title }
}

export const getIsTheatrical = (slug: string) => {
  return [slugs.soundOfFreedom, slugs.david].includes(slug)
}
